import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { organizationAPI, bodyAPI, userAPI, templateAPI, emailAPI, shareholderAPI, corporateAPI } from '../api';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Building2, Users, FileText, Brain, Scale, Mail, Pen, Crown, ClipboardList, Loader2, AlertTriangle
} from 'lucide-react';
import {
  OrganizationTab, BodiesTab, UsersTab, TemplatesTab,
  AISettingsTab, RCCTab, EmailSettingsTab, CorporateDocsTab,
} from '../components/admin';
import SignatureManager from '../components/SignatureManager';
import BoardComposition from '../components/BoardComposition';
import { toast } from 'sonner';

const Admin = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('organization');

  // Shared data
  const [bodies, setBodies] = useState([]);
  const [orgUsers, setOrgUsers] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [shareholders, setShareholders] = useState([]);

  // Org form (shared by Organization, AI, RCC tabs)
  const [orgForm, setOrgForm] = useState({
    name: '', timezone: 'Asia/Manila', ai_enabled: false, ai_api_key: '',
    ai_minutes_enabled: true, ai_resolutions_enabled: true, ai_certificates_enabled: true, ai_notices_enabled: true,
    resolution_numbering_pattern: '{BODY}-{YEAR}-{SEQ}',
    remote_meetings_allowed: true, principal_office: '',
    bylaw_board_notice_days: 7, bylaw_asm_notice_days: 21, bylaw_ssm_notice_days: 7,
    bylaw_board_quorum_type: 'majority_of_members',
    bylaw_stockholders_quorum_type: 'majority_of_outstanding_capital_stock',
    bylaw_board_meeting_frequency: 'monthly',
  });

  // Email config
  const [emailConfig, setEmailConfig] = useState({
    smtp_host: '', smtp_port: 587, smtp_email: '', smtp_password: '',
    display_name: '', use_tls: true, enabled: true,
  });
  const [emailLogs, setEmailLogs] = useState([]);
  const [emailConfigured, setEmailConfigured] = useState(false);

  // Corporate docs
  const [corpCredentials, setCorpCredentials] = useState([]);
  const [corpRequirements, setCorpRequirements] = useState([]);
  const [corpResources, setCorpResources] = useState([]);

  useEffect(() => {
    if (user?.organization_id) fetchData();
    else setLoading(false);
  }, [user]);

  const fetchData = async () => {
    try {
      const [orgRes, bodiesRes, usersRes, templatesRes] = await Promise.all([
        organizationAPI.get(user.organization_id),
        bodyAPI.getAll(),
        userAPI.getAll(),
        templateAPI.getAll(),
      ]);

      setBodies(bodiesRes.data);
      setOrgUsers(usersRes.data);
      setTemplates(templatesRes.data);

      try { const shRes = await shareholderAPI.getAll(); setShareholders(shRes.data || []); } catch {}

      try {
        const [emailRes, emailLogsRes] = await Promise.all([emailAPI.getConfig(), emailAPI.getLogs()]);
        if (emailRes.data.configured) {
          setEmailConfigured(true);
          setEmailConfig({
            smtp_host: emailRes.data.smtp_host || '', smtp_port: emailRes.data.smtp_port || 587,
            smtp_email: emailRes.data.smtp_email || '', smtp_password: emailRes.data.smtp_password || '',
            display_name: emailRes.data.display_name || '', use_tls: emailRes.data.use_tls !== false, enabled: emailRes.data.enabled !== false,
          });
        }
        setEmailLogs(emailLogsRes.data || []);
      } catch {}

      try {
        const [credRes, reqRes, resRes] = await Promise.all([
          corporateAPI.getCredentials(), corporateAPI.getRequirements(), corporateAPI.getResources(),
        ]);
        setCorpCredentials(credRes.data || []);
        setCorpRequirements(reqRes.data || []);
        setCorpResources(resRes.data || []);
      } catch {}

      setOrgForm({
        name: orgRes.data.name || '', timezone: orgRes.data.timezone || 'Asia/Manila',
        ai_enabled: orgRes.data.ai_enabled || false, ai_api_key: orgRes.data.ai_api_key || '',
        ai_minutes_enabled: orgRes.data.ai_minutes_enabled !== false,
        ai_resolutions_enabled: orgRes.data.ai_resolutions_enabled !== false,
        ai_certificates_enabled: orgRes.data.ai_certificates_enabled !== false,
        ai_notices_enabled: orgRes.data.ai_notices_enabled !== false,
        resolution_numbering_pattern: orgRes.data.resolution_numbering_pattern || '{BODY}-{YEAR}-{SEQ}',
        remote_meetings_allowed: orgRes.data.remote_meetings_allowed !== false,
        principal_office: orgRes.data.principal_office || '',
        bylaw_board_notice_days: orgRes.data.bylaw_board_notice_days ?? 7,
        bylaw_asm_notice_days: orgRes.data.bylaw_asm_notice_days ?? 21,
        bylaw_ssm_notice_days: orgRes.data.bylaw_ssm_notice_days ?? 7,
        bylaw_board_quorum_type: orgRes.data.bylaw_board_quorum_type || 'majority_of_members',
        bylaw_stockholders_quorum_type: orgRes.data.bylaw_stockholders_quorum_type || 'majority_of_outstanding_capital_stock',
        bylaw_board_meeting_frequency: orgRes.data.bylaw_board_meeting_frequency || 'monthly',
      });
    } catch (err) {
      console.error('Failed to fetch admin data:', err);
    } finally { setLoading(false); }
  };

  const handleOrgSave = async () => {
    setSaving(true);
    try {
      await organizationAPI.update(user.organization_id, orgForm);
      toast.success('Organization settings saved');
    } catch { toast.error('Failed to save settings'); }
    finally { setSaving(false); }
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-[60vh]"><Loader2 className="h-8 w-8 animate-spin text-[#00A3A3]" /></div>;
  }

  if (!user?.organization_id) {
    return (
      <div className="text-center py-16">
        <Building2 className="h-12 w-12 mx-auto mb-4 text-[#A8B1B9]" />
        <h2 className="text-xl font-semibold mb-2">No Organization</h2>
        <p className="text-[#5A6570]">Please complete onboarding to set up your organization.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-page">
      <div>
        <h1 className="text-2xl font-bold text-[#1A1D21]">Secretariat Settings</h1>
        <p className="text-[#5A6570] mt-1">Manage your organization, bodies, users, and templates</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="tabs-grid-5 bg-[#F0F2F4] p-1">
          <TabsTrigger value="organization" className="data-[state=active]:bg-white"><Building2 className="h-4 w-4 mr-1.5" />Organization</TabsTrigger>
          <TabsTrigger value="bodies" className="data-[state=active]:bg-white"><Users className="h-4 w-4 mr-1.5" />Bodies</TabsTrigger>
          <TabsTrigger value="users" className="data-[state=active]:bg-white"><Users className="h-4 w-4 mr-1.5" />Users & Roles</TabsTrigger>
          <TabsTrigger value="templates" className="data-[state=active]:bg-white"><FileText className="h-4 w-4 mr-1.5" />Templates</TabsTrigger>
          <TabsTrigger value="corporate" className="data-[state=active]:bg-white" data-testid="corporate-docs-tab"><ClipboardList className="h-4 w-4 mr-1.5" />Corp Docs</TabsTrigger>
          <TabsTrigger value="ai" className="data-[state=active]:bg-white"><Brain className="h-4 w-4 mr-1.5" />AI Settings</TabsTrigger>
          <TabsTrigger value="rcc" className="data-[state=active]:bg-white"><Scale className="h-4 w-4 mr-1.5" />RCC</TabsTrigger>
          <TabsTrigger value="email" className="data-[state=active]:bg-white" data-testid="email-settings-tab"><Mail className="h-4 w-4 mr-1.5" />Email</TabsTrigger>
          <TabsTrigger value="signature" className="data-[state=active]:bg-white" data-testid="signature-tab"><Pen className="h-4 w-4 mr-1.5" />E-Signature</TabsTrigger>
          <TabsTrigger value="board" className="data-[state=active]:bg-white" data-testid="board-composition-tab"><Crown className="h-4 w-4 mr-1.5" />Board</TabsTrigger>
        </TabsList>

        <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-3" data-testid="legal-disclaimer-banner">
          <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-amber-800">
            <strong>Legal Notice:</strong> LexAgenda is a governance and record-management tool. It supports but does not replace legal advice or the Corporate Secretary's statutory responsibilities under RA 11232 (Revised Corporation Code) and SEC regulations.
          </p>
        </div>

        <TabsContent value="organization" className="space-y-4">
          <OrganizationTab orgForm={orgForm} setOrgForm={setOrgForm} handleOrgSave={handleOrgSave} saving={saving} />
        </TabsContent>

        <TabsContent value="bodies" className="space-y-4">
          <BodiesTab bodies={bodies} organizationId={user.organization_id} fetchData={fetchData} />
        </TabsContent>

        <TabsContent value="users" className="space-y-4">
          <UsersTab orgUsers={orgUsers} bodies={bodies} shareholders={shareholders} fetchData={fetchData} />
        </TabsContent>

        <TabsContent value="templates" className="space-y-4">
          <TemplatesTab templates={templates} user={user} fetchData={fetchData} />
        </TabsContent>

        <TabsContent value="ai" className="space-y-4">
          <AISettingsTab orgForm={orgForm} setOrgForm={setOrgForm} handleOrgSave={handleOrgSave} saving={saving} />
        </TabsContent>

        <TabsContent value="rcc" className="space-y-4" data-testid="rcc-compliance-tab">
          <RCCTab orgForm={orgForm} setOrgForm={setOrgForm} handleOrgSave={handleOrgSave} saving={saving} />
        </TabsContent>

        <TabsContent value="email" className="space-y-4" data-testid="email-settings-content">
          <EmailSettingsTab emailConfig={emailConfig} setEmailConfig={setEmailConfig} emailLogs={emailLogs} setEmailLogs={setEmailLogs} emailConfigured={emailConfigured} setEmailConfigured={setEmailConfigured} />
        </TabsContent>

        <TabsContent value="signature" className="space-y-4" data-testid="signature-tab-content">
          <SignatureManager />
        </TabsContent>

        <TabsContent value="board" className="space-y-4" data-testid="board-composition-tab-content">
          <BoardComposition />
        </TabsContent>

        <TabsContent value="corporate" className="space-y-6" data-testid="corporate-docs-tab-content">
          <CorporateDocsTab corpCredentials={corpCredentials} corpRequirements={corpRequirements} corpResources={corpResources} fetchData={fetchData} />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Admin;
